import {
  AwardPointsOrVouchersInput,
  DirtyFieldsType,
  Operation,
} from '@occc-app/offerHelpers/types';
import {
  OfferFragment,
  PromocodeBatchCreateMutationVariables,
  PromocodeBatchDeleteMutationVariables,
  PromocodeBatchUpdateMutationVariables,
  PromocodesOfferType,
} from '@occc/graphql';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeEarningMechanismUpdateOperation } from '@occc-app/offerHelpers/operations/earningMechanism';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';

import {
  PromocodeFormValues,
  PromocodesFormValues,
  isPromocodesGlobalFormValues,
  PromocodesTargetFormValues,
} from '../PromocodesConfiguration/promocodes-configuration.types';

type BatchCreateOperationType = Operation<
  'promocodeBatchCreate',
  PromocodeBatchCreateMutationVariables
>;
type BatchUpdateOperationType = Operation<
  'promocodeBatchUpdate',
  PromocodeBatchUpdateMutationVariables
>;
type BatchDeleteOperationType = Operation<
  'promocodeBatchDelete',
  PromocodeBatchDeleteMutationVariables
>;
const toCreateInput = (promocode: PromocodeFormValues) => ({
  code: promocode.code,
  description: promocode.description || null,
  startDate: promocode.startDate.toISOString(),
  endDate: promocode.endDate?.toISOString() ?? null,
});

const toUpdateInput = (promocode: PromocodeFormValues) => ({
  id: promocode.id,
  code: promocode.code,
  description: promocode.description || null,
  startDate: promocode.startDate.toISOString(),
  endDate: promocode.endDate?.toISOString() ?? null,
});

export const getPromocodesAwardInputFromOffer = (
  offer: OfferFragment | undefined,
): AwardPointsOrVouchersInput | null => {
  const earningMechanism = offer?.earningMechanism;

  if (earningMechanism?.__typename !== 'EarningMechanismPromocodes' || !earningMechanism.award) {
    return null;
  }

  const { award } = earningMechanism;

  if (awardHelper.isPointsAward(award)) {
    return { awardPoints: { points: award.points } };
  }

  if (awardHelper.isVouchersAward(award)) {
    return {
      awardVouchers: {
        voucheredOfferId: award.voucheredOffer.id,
        voucherCount: 1,
        voucherExpirationDays: award.voucherExpirationDays ?? null,
      },
    };
  }

  return null;
};

export const makePromocodesCodesTypeUpdateOperation = (
  offer: OfferFragment | undefined,
  codesType: PromocodesOfferType,
): Operation | null => {
  const earningMechanism = offer?.earningMechanism;

  if (earningMechanism?.__typename !== 'EarningMechanismPromocodes' || !earningMechanism.id) {
    return null;
  }

  const award = getPromocodesAwardInputFromOffer(offer);
  const configuration = earningMechanismInputCreator.createPromocodes(award, codesType);

  return makeEarningMechanismUpdateOperation(earningMechanism.id, configuration);
};

const getExistingPromocodes = (offer: OfferFragment | undefined) => {
  if (offer?.earningMechanism?.__typename !== 'EarningMechanismPromocodes') {
    return [];
  }

  return offer.earningMechanism.promocodes;
};

const getPromoCodeBatchOperations = (
  offer: OfferFragment,
  promocodesFormValues: PromocodesFormValues,
): Operation[] => {
  if (!isPromocodesGlobalFormValues(promocodesFormValues)) {
    return [];
  }

  const { id: offerId } = offer;
  const { promocodes } = promocodesFormValues;
  const promocodesToUpdate = promocodes.filter((promocode) => promocode.isSaved);
  const promocodesToCreate = promocodes.filter(
    (promocode) => !promocode.isSaved && promocode.code.trim(),
  );
  const persistedPromocodes = getExistingPromocodes(offer);
  const persistedIdsInForm = new Set(promocodesToUpdate.map((promocode) => promocode.id));
  const promocodesToDelete = persistedPromocodes.filter(
    (promocode) =>
      !persistedIdsInForm.has(promocode.id) && (promocode.stats?.redemptions ?? 0) === 0,
  );

  const operations: Operation[] = [];

  if (promocodesToCreate.length > 0) {
    const op: BatchCreateOperationType = {
      name: 'promocodeBatchCreate',
      variables: {
        input: {
          offerId,
          promocodes: promocodesToCreate.map(toCreateInput),
        },
      },
    };
    operations.push(op);
  }

  if (promocodesToUpdate.length > 0) {
    const op: BatchUpdateOperationType = {
      name: 'promocodeBatchUpdate',
      variables: {
        input: promocodesToUpdate.map(toUpdateInput),
      },
    };
    operations.push(op);
  }

  if (promocodesToDelete.length > 0) {
    const op: BatchDeleteOperationType = {
      name: 'promocodeBatchDelete',
      variables: {
        ids: promocodesToDelete.map((promocode) => promocode.id),
      },
    };
    operations.push(op);
  }

  return operations;
};

export const getPromocodesOperations = (
  _offerId: string,
  offer: OfferFragment | undefined,
  values: PromocodesTargetFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  if (
    !isFieldDirty<PromocodesTargetFormValues>(dirtyFields, 'promocodes') ||
    !values.promocodes ||
    !offer
  ) {
    return [];
  }

  const operations: Operation[] = [];
  const codesTypeOperation = makePromocodesCodesTypeUpdateOperation(
    offer,
    values.promocodes.codesType,
  );

  if (codesTypeOperation) {
    operations.push(codesTypeOperation);
  }

  operations.push(...getPromoCodeBatchOperations(offer, values.promocodes));

  return operations;
};
